import { kebabCase } from 'lodash-es';
import { DashboardWidget, DashboardWidgetFC, useDashboardWidget } from '@frontend/grid-dashboard';
import { i18next } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { NakedA, SlideCarousel, Text, styles } from '@frontend/design-system';
import NewWeaveTrainingCampImage from './assets/new-weave-training-camp.svg';
import NewWeave from './assets/new-weave.webp';
import SeamlessTransition from './assets/seamless-transition.svg';
import TrainingWeek from './assets/training-week.svg';

const TrainingLinks = [
  {
    title: i18next.t('Become an expert on the new Weave experience', { ns: 'onboarding-modules' }),
    description: i18next.t(
      'Join us for Weave Training Week to unlock new tools and supercharge your workflow and drive success!',
      { ns: 'onboarding-modules' }
    ),
    link: 'https://events.zoom.us/ev/AvFlHuDZ7PybjklzZVP_cuJExnfwomIinV775xyPE09r_b94jWiz~AsmcmOHeTSn1nTz0gJ1Zi3mzbjfgw8oRXF6xdpkbPnzM4jBw2TxVcgOVqA',
    image: TrainingWeek,
    altText: i18next.t('New Weave Experience Image', { ns: 'onboarding-modules' }),
  },
  {
    title: i18next.t('You don’t have to figure it out alone', { ns: 'onboarding-modules' }),
    description: i18next.t(
      'The New Weave was built with your feedback and your day-to-day needs in mind. In this live workshop, we’ll guide you through what’s new and what’s familiar so that you can feel confident with Weave: no tech-speak, no pressure, just practical help from people who understand your workflow.',
      { ns: 'onboarding-modules' }
    ),
    link: 'https://events.zoom.us/ev/Ak-o4O7tfFycchv0aF6egRw8SnZzzvlfce4srSR55lDoq7F3A7Hu~AmvRE0jiNS8e0N7C-r0A8vliNF-5RSd-qDxLzV-vPS3QQ6zZhaRLIs-y6w',
    image: SeamlessTransition,
    altText: i18next.t('New Weave Experience Image', { ns: 'onboarding-modules' }),
  },

  {
    title: i18next.t('Getting Around the New Weave Experience', { ns: 'onboarding-modules' }),
    description: i18next.t(
      'With the new Weave experience, you can communicate with customers, see call history, and more within a browser on your computer or a new desktop app.',
      { ns: 'onboarding-modules' }
    ),
    link: 'https://www.weavehelp.com/weavehelp/article/getting-around-the-weave-app',
    image: NewWeave,
    altText: i18next.t('New Weave Experience Image', { ns: 'onboarding-modules' }),
  },
  {
    title: i18next.t('New Weave Courses', { ns: 'onboarding-modules' }),
    description: i18next.t(
      'Find training courses for New Weave here. Take courses in recommended order by opening Training Drills, or search for specific courses by clicking See All Courses.',
      { ns: 'onboarding-modules' }
    ),
    link: 'https://trainingcamp.weavehelp.com/',
    image: NewWeaveTrainingCampImage,
    altText: i18next.t('New Weave Experience Courses Image', { ns: 'onboarding-modules' }),
  },
];

/**
 * @dashboard-widget
 *
 * id: training-articles
 * title: Training
 * description: See helpful resources as you learn all about Weave's app.
 * icon: graduation-cap-small
 */
export const TrainingDashboardWidget: DashboardWidgetFC = () => {
  const { widgetTrackingId } = useDashboardWidget();

  return (
    <DashboardWidget>
      <DashboardWidget.Header />
      <DashboardWidget.Content css={{ display: 'flex', flexDirection: 'column', flex: 1, marginTop: theme.spacing(1) }}>
        <SlideCarousel slides={TrainingLinks.map(({ title }) => title)}>
          <SlideCarousel.Container>
            {TrainingLinks.map(({ altText, title, description, image, link }) => (
              <SlideCarousel.Panel key={title} id={title}>
                <NakedA
                  trackingId={widgetTrackingId(kebabCase(title))}
                  href={link}
                  rel='noopener noreferrer'
                  target='_blank'
                  css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}
                >
                  <img
                    src={image}
                    width='100%'
                    height='auto'
                    alt={altText}
                    css={{ maxHeight: 136, objectFit: 'cover' }}
                  />
                  <Text size='large' weight='bold'>
                    {title}
                  </Text>
                  <Text size='medium' css={styles.multilineTruncate(4)}>
                    {description}
                  </Text>
                </NakedA>
              </SlideCarousel.Panel>
            ))}
          </SlideCarousel.Container>
          <SlideCarousel.Navigation />
        </SlideCarousel>
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

TrainingDashboardWidget.config = {
  size: 'large-narrow',
  feature: 'general',
};
